import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { ArrowBack } from '@mui/icons-material';
import {
    Container,
    Content,
    Title,
    i18n,
    tokens,
    Loader,
    Theme,
    Button,
    ChangeRequestForm,
    MessageCard
} from '@simplybenefits/sb-ui-library';
import RequestedChangesTable from './RequestedChangesTable.js';
import {
    getChangeRequests,
    downloadChangeRequestPdf,
    resendChangeRequestLink,
    approveChangeRequest,
    declineChangeRequest,
    getChangeRequestFile
} from '../../../actions/changeRequests.js';
import {
    listFiles,
    createFolder,
    uploadFile,
    downloadFile,
    removeFile
} from '../../../actions/files.js';
import * as notifier from '../../../actions/notifier.js';

// Styles object for inline styles
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 20
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    },
    arrowBack: {
        cursor: 'pointer',
        fontSize: '50px',
        marginRight: 1.25,
        paddingBottom: '10px',
        color: Theme.colors.darkGrey
    },
    button: {
        height: 50,
        alignSelf: 'center'
    }
};

/**
 * Index component for managing change requests.
 *
 * @param {Object} props - Component properties.
 * @param {Function} props.dispatch - Redux dispatch function.
 * @param {Object} props.loader - Loader state from Redux.
 * @param {Object} props.meta - Meta data from Redux.
 * @returns {JSX.Element} The rendered component.
 */
const Index = ({ dispatch, loader, meta }) => {
    const [ requestedChanges, setRequestedChanges ] = useState([]);
    const [ changeRequest, setChangeRequest ] = useState({
        changeRequestID: null,
        changeRequestFile: null
    });
    const [ displayChangeRequestForm, setDisplayChangeRequestForm ] = useState(false);
    const [ cursor, setCursor ] = useState(0);
    const topRef = useRef(null);

    /**
     * Fetches change requests from the server.
     *
     * @private
     * @returns {Promise<void>}
     */
    const fetchChangeRequests = async () => {
        const result = await dispatch(getChangeRequests(cursor));
        const items = cursor > 0 ? [ ...requestedChanges, ...result.items ] : result.items;
        setRequestedChanges(items);
        setCursor(result.cursor);
    };

    useEffect(() => {
        fetchChangeRequests();

        const queryString = window.location.search;
        if (queryString.includes('success')) {
            dispatch(notifier.displayMessage(i18n.t(tokens.labels.changeRequests.changeRequestSuccessfullySent)));
        }
    }, []);

    /**
     * Handles the display of the change request form.
     * 
     * @param {Object} changeRequestData - Data for the selected change request.
     * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating success or failure.
     */
    const handleChangeFormDisplay = async (changeRequestData) => {
        try {
            const status = changeRequestData.status.toLowerCase();
            if ([ 'approved', 'declined' ].includes(status)) {
                const url = await dispatch(downloadChangeRequestPdf(changeRequestData.changeRequestID));

                if (typeof url === 'string') {
                    window.open(url, '_blank');
                }

                return true;
            }

            const changeRequestFile = await dispatch(getChangeRequestFile(changeRequestData.changeRequestID));

            if (changeRequestFile) {
                setChangeRequest(() => ({
                    changeRequestID: changeRequestData.changeRequestID,
                    changeRequestFile
                }));

                setDisplayChangeRequestForm(true);
            }

            return true;
        } catch (error) {
            console.error("Error getting the change request file", error);
            return false;
        }
    };

    /**
     * Resends the review request email.
     *
     * @returns {Promise<void>}
     */
    const resendReviewRequest = async () => {
        if (changeRequest.changeRequestFile.status.toLowerCase() === 'pending admin review') {
            await dispatch(resendChangeRequestLink(changeRequest.changeRequestID));
        }
    };

    /**
     * Handles going back to the requested changes list.
     */
    const goBack = () => {
        setChangeRequest({
            changeRequestID: null,
            changeRequestFile: null
        });

        setDisplayChangeRequestForm(false);
    };

    /**
     * 
     * @param {*} submitData Data returned from the change request form
     * @param {*} submitType Approve or Decline
     * @returns 
     */
    const submit = async (submitData, submitType) => {
        let successData = null;
    
        if (submitType === 'Approve') {
            successData = await dispatch(approveChangeRequest(changeRequest.changeRequestID, submitData));
        } else if (submitType === 'Decline') {
            successData = await dispatch(declineChangeRequest(changeRequest.changeRequestID, submitData));
        }

        if (successData.updatedChangeRequestFile) {
            setChangeRequest(() => ({
                changeRequestID: changeRequest.changeRequestID,
                changeRequestFile: successData.updatedChangeRequestFile
            }));

            // Update the state of requestedChanges
            setRequestedChanges((prevChanges) => prevChanges.map((change) => change.changeRequestID === changeRequest.changeRequestID 
                ? { ...change, status: successData.updatedChangeRequestFile.status } 
                : change));

            topRef.current.scrollIntoView({ behavior: 'smooth' });
            return true;
        }

        return false;
    };

    /**
     * 
     * @param {*} status 
     * @returns 
     */
    const returnMessageCard = (status) => {
        if (status.toLowerCase() === 'pending member review') {
            return (
                <MessageCard
                    title={ i18n.t(tokens.labels.changeRequests.reviewChangeRequest) }
                    subtitle={ i18n.t(tokens.labels.changeRequests.reviewChangeRequestBodyMember) }
                />
            );
        }

        return (
            <MessageCard
                title={ i18n.t(tokens.labels.employees.pending) }
                subtitle={ i18n.t(tokens.labels.changeRequests.changeRequestPendingReview) }
            />
        );
    };

    return (
        <Container ref={ topRef }>
            <Content>
                <div style={styles.container}>
                    <div style={styles.titleContainer}>
                        {
                            displayChangeRequestForm && (
                                <ArrowBack
                                    onClick={goBack}
                                    sx={styles.arrowBack}
                                />
                            )
                        }
                        <Title title={i18n.t(tokens.labels.amendments.requestedChanges)} />
                    </div>
                    {
                        displayChangeRequestForm && changeRequest.changeRequestFile.status.toLowerCase() === 'pending admin review' && (
                            <Button
                                color="primary"
                                onClick={resendReviewRequest}
                                customStyle={styles.button}
                            >
                                {i18n.t(tokens.buttons.resendReviewRequest)}
                            </Button>
                        )
                    }
                </div>

                {
                    !displayChangeRequestForm
                        ? (
                            <RequestedChangesTable
                                requestedChanges={requestedChanges}
                                openChangeRequest={handleChangeFormDisplay}
                                loadMoreRequestedChanges={fetchChangeRequests}
                            />
                        )
                        : (
                            <>
                                { returnMessageCard(changeRequest.changeRequestFile.status) }
                                <ChangeRequestForm
                                    id={meta.userID}
                                    accessType="member"
                                    data={changeRequest.changeRequestFile}
                                    type="users"
                                    dispatch={dispatch}
                                    loader={loader}
                                    listFiles={listFiles}
                                    createFolder={createFolder}
                                    uploadFile={uploadFile}
                                    downloadFile={downloadFile}
                                    removeFile={removeFile}
                                    approveChangeRequest={(...params) => submit(...params, 'Approve')}
                                    declineChangeRequest={(...params) => submit(...params, 'Decline')}
                                />
                            </>
                        )
                }

                {loader?.isLoading && <Loader />}
            </Content>
        </Container>
    );
};

/**
 * Maps Redux state to component props.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object} The mapped props.
 */
const mapStateToProps = (state) => ({
    whitelabel: state.user.whitelabel,
    loader: state.loader,
    meta: state.user.meta
});

export default connect(mapStateToProps)(Index);